import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import { GlassCard } from '@/components/GlassCard';
import { Loader2, KeyRound, CheckCircle2, AlertTriangle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Seo } from '@/components/Seo';

export default function ResetPasswordPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [validating, setValidating] = useState(true);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [tokenError, setTokenError] = useState(null);

  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!token) {
        setTokenError('Missing reset token in the link.');
        setValidating(false);
        return;
      }
      try {
        const { data } = await api.get('/auth/reset-password/validate', { params: { token } });
        if (!cancelled) setTokenInfo(data);
      } catch (e) {
        if (!cancelled) setTokenError(e?.response?.data?.detail || 'This reset link is invalid or expired.');
      } finally {
        if (!cancelled) setValidating(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (pwd !== pwd2) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pwd });
      setDone(true);
      toast.success('Password updated. You can sign in now.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reset password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative min-h-screen th-hero-radial">
      <Seo
        title="Reset Password | Thirrsty.com"
        description="Set a new password for your Thirrsty.com account."
        path="/auth/reset-password"
        noindex
      />
      <div className="mx-auto flex min-h-screen max-w-md flex-col px-6 py-10">
        <div className="mb-6"><Logo asDownloadTrigger /></div>

        <GlassCard className="p-6 th-float-in">
          <button
            type="button"
            onClick={() => nav('/auth/login')}
            className="mb-4 inline-flex items-center gap-1 text-xs text-white/65 hover:text-white"
            data-testid="reset-back-button"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
          </button>

          {validating && (
            <div className="py-8 text-center" data-testid="reset-validating-block">
              <Loader2 className="mx-auto h-6 w-6 th-spin text-[#F6B24A]" />
              <p className="mt-3 text-sm text-white/70">Checking your reset link…</p>
            </div>
          )}

          {!validating && tokenError && (
            <div className="text-center" data-testid="reset-error-block">
              <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-red-500/15 text-red-300">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h2 className="font-display text-xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>Link no longer valid</h2>
              <p className="mt-2 text-sm text-white/70">{tokenError}</p>
              <Button
                onClick={() => nav('/auth/forgot')}
                className="mt-5 h-11 w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold"
                data-testid="reset-request-new-button"
              >
                Request a new reset link
              </Button>
            </div>
          )}

          {!validating && !tokenError && !done && (
            <>
              <h1 className="font-display text-3xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Set new password</h1>
              <p className="mt-1 text-sm text-white/70">
                Resetting password for <span className="text-white font-medium">{tokenInfo?.email}</span>.
                This link expires in <span className="text-[#F6B24A]">15 minutes</span>.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="pwd" className="text-white/80">New password</Label>
                  <Input
                    id="pwd" type="password" required minLength={6}
                    value={pwd} onChange={(e) => setPwd(e.target.value)}
                    placeholder="At least 6 characters"
                    className="mt-1 h-11 bg-white/5 border-white/12 text-white placeholder:text-white/40"
                    data-testid="reset-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="pwd2" className="text-white/80">Confirm password</Label>
                  <Input
                    id="pwd2" type="password" required minLength={6}
                    value={pwd2} onChange={(e) => setPwd2(e.target.value)}
                    placeholder="Repeat your new password"
                    className="mt-1 h-11 bg-white/5 border-white/12 text-white placeholder:text-white/40"
                    data-testid="reset-password-confirm-input"
                  />
                </div>
                <Button
                  type="submit" disabled={saving || pwd.length < 6 || pwd !== pwd2}
                  className="h-11 w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold disabled:opacity-50"
                  data-testid="reset-submit-button"
                >
                  {saving ? <Loader2 className="h-4 w-4 th-spin" /> : (<><KeyRound className="mr-2 h-4 w-4" /> Update password</>)}
                </Button>
              </form>
            </>
          )}

          {done && (
            <div className="text-center py-2" data-testid="reset-success-block">
              <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-emerald-500/15 text-emerald-300">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <h2 className="font-display text-xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>Password updated</h2>
              <p className="mt-2 text-sm text-white/70">You can now sign in with your new password.</p>
              <Button
                onClick={() => nav('/auth/login')}
                className="mt-5 h-11 w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold"
                data-testid="reset-go-login-button"
              >
                Go to sign in
              </Button>
            </div>
          )}
        </GlassCard>

        <p className="mt-auto pt-8 text-center text-xs text-white/40">
          © Banks Class LLC · <Link to="/privacy" className="underline">Privacy</Link> · <Link to="/terms" className="underline">Terms</Link> · <Link to="/contact" className="underline">Contact Us</Link>
        </p>
      </div>
    </div>
  );
}
